/**
 * Component for initiating and managing screen recording during a SIM session.
 *
 * @module components/sim/takeSim/ScreenRecording
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    FormGroup,
    FormControlLabel,
    //Checkbox,
    FormControl,
    FormLabel,
    RadioGroup,
    Typography,
    Radio,
    Button,
    DialogActions,
    Grid,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { enableVideoRecord } from '../../../redux/sims/actions';

/**
 * Represents the screen recording functionality component.
 *
 * This component is responsible for managing the screen recording process,
 * including enabling recording, configuring the number of screens to record,
 * and handling user interactions via dialogs and buttons. It provides a dialog
 * interface for prompting the user to initiate recording and captures their preferences.
 *
 * Properties:
 * - **capture**: A callback function invoked to start the video recording process.
 * - **setRecPerDialog**: A function to update the state of the recording permission dialog (open/close).
 * - **dialogClose**: A callback function that handles the closure of the dialog.
 * - **recPerDialog**: A boolean value representing whether the recording permission dialog is open.
 * - **setRecPer**: A function to change the permission status for recording.
 * - **totalScreens**: Represents the current selection of total screens to be recorded simultaneously.
 * - **setTotalScreens**: A function to update the number of screens selected for recording.
 * - **classes**: Object containing style classes for styling the component.
 * - **enableVideoRecord**: A function to activate or deactivate the video recording feature.
 */
const ScreenRecording = ({
    capture,
    setRecPerDialog,
    dialogClose,
    recPerDialog,
    setRecPer,
    totalScreens,
    setTotalScreens,
    classes,
    enableVideoRecord
}) => {
    const [recordingEnabled, setRecordingEnabled] = React.useState(false);

    /**
     * Enables the recording functionality within the application.
     *
     * This function activates the recording mode by setting the internal
     * state or flag that controls whether recording is allowed. Once called,
     * any subsequent logic dependent on the recording state will behave
     * as though recording is enabled.
     */
    const enableRecording = () => {
        setRecordingEnabled(true);
    };

    /**
     * Handles the process of starting or managing a recording session.
     *
     * This function performs the following actions:
     * - Enables video recording by invoking `enableVideoRecord` with a `true` parameter.
     * - Disables or closes the recording permission dialog by setting `setRecPerDialog` to `false`.
     * - Updates the recording permission state by setting `setRecPer` to `true`.
     * - Initiates the video capture process by calling the `capture` function.
     */
    const handleRecording = () => {
        enableVideoRecord(true);
        setRecPerDialog(false);
        setRecPer(true);
        capture();
    };

    return (
        <Dialog onClose={dialogClose} open={recPerDialog}>
            <DialogTitle>Screen Recording</DialogTitle>
            <DialogContent>
                {!recordingEnabled && (
                    <div>
                        <p>Do you want to start screen recording for this particular SIM?</p>
                        <FormGroup>
                            <Grid container justifyContent="space-around" spacing={2}>
                                <Grid item xs={4}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={enableRecording}
                                    >
                                        Yes
                                    </Button>
                                </Grid>
                                <Grid item xs={4}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        className={classes.cancel}
                                        onClick={dialogClose}
                                    >
                                        No
                                    </Button>
                                </Grid>
                            </Grid>
                        </FormGroup>
                    </div>
                )}
                {recordingEnabled && (
                    <div>
                        <FormControl>
                            <FormLabel component="legend">
                                How many screens do you want to record simultaneously?
                            </FormLabel>
                            <RadioGroup
                                aria-label="total screens recording"
                                value={totalScreens}
                                onChange={(e) => setTotalScreens(e.target.value)}
                                style={{marginTop: '1rem'}}
                            >
                                <Grid container spacing={2}>
                                    <FormControlLabel value="1" control={<Radio/>} label="1"/>
                                    <FormControlLabel value="2" control={<Radio />} label="2" />
                                </Grid>
                            </RadioGroup>
                        </FormControl>
                        <Typography className={classes.warnTypo}>
                            <InfoOutlinedIcon className={classes.info} />
                            {' '}
                            The screen
                            recording cannot be disabled in between recording of the video
                        </Typography>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                {recordingEnabled && (
                    <>
                        <Button
                            style={{ float: 'right', marginRight: '5px' }}
                            variant="outlined"
                            color="primary"
                            size="small"
                            className={classes.cancel}
                            onClick={dialogClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            style={{ float: 'right', marginRight: '5px' }}
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleRecording}
                        >
                            Proceed
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
ScreenRecording.propTypes = {
    capture: PropTypes.func,
    totalScreens: PropTypes.any,
    setRecPerDialog: PropTypes.func,
    setTotalScreens: PropTypes.func,
    classes: PropTypes.object,
    setRecPer: PropTypes.func,
    dialogClose: PropTypes.func,
    recPerDialog: PropTypes.bool,
    recPer: PropTypes.bool,
    enableVideoRecord: PropTypes.func,
};

const mapStateToProps = (state) => ({
    levelClips: state.sim.levelClips,
    levelClipsTemp: state.sim.levelClipsTemp,
    levels: state.sim.stats.result.levels,
    simId: state.sim.stats.result.sim_id,
    userResponse: state.sim.userResponse,
    simTitle: state.sim.simTitle,
});

const mapDispatchToProps = {
    enableVideoRecord,
};

// connect Sims component to redux store

export default connect(mapStateToProps, mapDispatchToProps)(ScreenRecording);
